import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { Message } from 'element-ui'
import { getSettleStatus } from '@/api/settle'
import { pushCurrentUrl } from '@/utils/baiduPush'

Vue.use(VueRouter)

// 站点域名，用于 canonical 链接构造
const SITE_ORIGIN = 'https://pointcloudyzw.com'

/**
 * 生成规范的 head 配置（供 vue-meta 使用）
 * 统一管理 title / description / keywords / canonical，避免每条路由重复书写
 * @param {object} opts
 * @param {string} opts.title 页面标题
 * @param {string} [opts.description] 页面描述
 * @param {string} [opts.keywords] 页面关键词
 * @param {string} [opts.path] 规范化路径，用于 canonical
 * @param {boolean} [opts.noindex] 是否禁止索引
 */
function makeHead(opts) {
  const head = {
    title: opts.title,
    meta: [
      {
        vmid: 'description',
        name: 'description',
        content: opts.description || '点云研智网是专业的3D定制服务与点云数字资产交易平台，提供场景点云、3D模型、三维软件定制、点云处理等服务。'
      }
    ]
  }
  if (opts.keywords) {
    head.meta.push({
      vmid: 'keywords',
      name: 'keywords',
      content: opts.keywords
    })
  }
  if (opts.path) {
    head.link = [
      {
        vmid: 'canonical',
        rel: 'canonical',
        href: SITE_ORIGIN + (opts.path.startsWith('/') ? opts.path : '/' + opts.path)
      }
    ]
  }
  if (opts.noindex) {
    head.meta.push({
      vmid: 'robots',
      name: 'robots',
      content: 'noindex, nofollow'
    })
  }
  return head
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home'),
    meta: {
      title: '点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网是专业的3D定制服务与点云数字资产交易平台，提供场景点云、3D模型下载、三维软件开发、点云处理、3D打印、三维建模等定制服务，支持 LAS/PCD/E57/PTX/PLY/RCP 等多种点云格式与 3ds Max/Maya/Blender/OBJ/FBX/STL/Rhino/GLTF 等模型格式。',
        keywords: '3D点云,点云下载,3D模型,3D定制,点云处理,PCL,CloudCompare,三维建模,三维软件开发,LAS,PCD,E57,PTX,PLY,RCP,3ds Max,Maya,Blender,OBJ,FBX,STL,Rhino,GLTF,BIM,VTK,场景点云,建筑点云,逆向工程,数字孪生',
        path: '/'
      })
    }
  },
  {
    path: '/model',
    name: 'Model',
    component: () => import('@/views/Model'),
    meta: {
      title: '点云研智网-建筑模型 手办打印模型 场景模型 机械工业等模型免费下载',
      head: makeHead({
        title: '点云研智网-建筑模型 手办打印模型 场景模型 机械工业等模型免费下载',
        description: '点云研智网3D模型库，提供建筑模型、手办打印模型、场景模型、机械工业模型等免费3D模型下载，支持 3ds Max、Maya、Blender、OBJ、FBX、STL、Rhino、GLTF 等格式。',
        keywords: '3D模型,免费3D模型,模型下载,3ds Max模型,Maya模型,Blender模型,OBJ模型,FBX模型,STL模型,Rhino模型,GLTF模型,建筑模型,手办打印模型,场景模型,机械工业模型',
        path: '/model'
      })
    }
  },
  {
    path: '/model-detail/:modelId',
    name: 'ModelDetail',
    component: () => import('@/views/ModelDetail'),
    meta: {
      title: '模型详情 | 点云研智网-建筑模型 手办打印模型 场景模型 机械工业等模型免费下载',
      head: makeHead({
        title: '模型详情 | 点云研智网-建筑模型 手办打印模型 场景模型 机械工业等模型免费下载',
        description: '点云研智网3D模型详情页，查看并下载高质量3D模型，支持多种3D软件格式。',
        keywords: '3D模型,模型详情,模型下载,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/scene',
    name: 'Scene',
    component: () => import('@/views/Scene'),
    meta: {
      title: '点云研智网-场景点云 自然环境点云 建筑点云 植物等点云免费下载',
      head: makeHead({
        title: '点云研智网-场景点云 自然环境点云 建筑点云 植物等点云免费下载',
        description: '点云研智网点云库，提供场景点云、自然环境点云、建筑点云、植物点云等免费点云数据下载，支持 LAS、PCD、E57、PTX、PLY、RCP 等格式。',
        keywords: '3D点云,点云下载,免费点云,场景点云,自然环境点云,建筑点云,植物点云,LAS,PCD,E57,PTX,PLY,RCP,点云研智网',
        path: '/scene'
      })
    }
  },
  {
    path: '/product/:categoryId?',
    name: 'Product',
    component: () => import('@/views/Product'),
    meta: {
      title: '点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
      head: makeHead({
        title: '点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
        description: '点云研智网3D定制作品库，提供三维软件开发、三维建模、点云处理、3D打印、点云逆向建模等定制产品服务，快速匹配专业服务商。',
        keywords: '3D定制,三维软件开发,三维建模,点云处理,3D打印,点云逆向建模,定制服务,点云研智网',
        path: '/product'
      })
    }
  },
  {
    path: '/custom-service',
    name: 'CustomService',
    component: () => import('@/views/CustomService/index.vue'),
    meta: {
      title: '定制服务 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
      head: makeHead({
        title: '定制服务 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
        description: '点云研智网定制服务，发布定制需求快速匹配服务商，涵盖三维软件开发、三维建模、点云处理、3D打印、点云逆向建模等。',
        keywords: '3D定制,定制服务,发布需求,三维软件开发,三维建模,点云处理,3D打印,点云研智网',
        path: '/custom-service'
      })
    }
  },
  {
    path: '/custom-service-detail/:id',
    name: 'CustomServiceDetail',
    component: () => import('@/views/CustomService/Detail.vue'),
    meta: {
      title: '定制服务详情 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
      head: makeHead({
        title: '定制服务详情 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
        description: '查看定制服务详情，了解服务内容、价格与服务商信息。',
        keywords: '定制服务详情,3D定制,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/scene-detail/:workId',
    name: 'SceneDetail',
    component: () => import('@/views/SceneDetail'),
    meta: {
      title: '点云详情 | 点云研智网-场景点云 自然环境点云 建筑点云 植物等点云免费下载',
      head: makeHead({
        title: '点云详情 | 点云研智网-场景点云 自然环境点云 建筑点云 植物等点云免费下载',
        description: '查看点云详情，下载场景点云、建筑点云、自然环境点云等高质量点云数据。',
        keywords: '点云详情,点云下载,场景点云,建筑点云,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/works-detail/:workId',
    name: 'WorksDetail',
    component: () => import('@/views/ProductDetail'),
    meta: {
      title: '作品详情 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
      head: makeHead({
        title: '作品详情 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
        description: '查看3D定制作品详情，了解作品内容、价格与作者信息。',
        keywords: '作品详情,3D定制,定制服务,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/seller-join',
    name: 'SellerJoin',
    component: () => import('@/views/UserCenter/SellerJoin'),
    meta: {
      title: '商家招募 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '商家招募 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网商家招募入驻，欢迎3D定制服务商与点云数字资产创作者入驻平台。',
        keywords: '商家招募,商家入驻,3D定制服务商,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/seller-join-2',
    name: 'SellerJoin2',
    component: () => import('@/views/UserCenter/sellerJoin2'),
    meta: {
      title: '商家入驻 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '商家入驻 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网商家入驻流程，填写入驻信息完成商家认证。',
        keywords: '商家入驻,商家认证,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/real-name-auth',
    name: 'RealNameAuth',
    component: () => import('@/views/RealNameAuth'),
    meta: {
      title: '实名认证 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '实名认证 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网实名认证，保障账号安全与交易合规。',
        keywords: '实名认证,账号安全,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/personal-auth',
    name: 'PersonalAuth',
    component: () => import('@/views/RealNameAuth/PersonalAuth'),
    meta: {
      title: '个人实名认证 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '个人实名认证 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网个人实名认证，完成个人身份验证。',
        keywords: '个人实名认证,身份验证,点云研智网',
        noindex: true
      })
    }
  },
  // {
  //   path: '/enterprise-auth',
  //   name: 'EnterpriseAuth',
  //   component: () => import('@/views/RealNameAuth/EnterpriseAuth'),
  //   meta: {
  //     title: '企业实名认证 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
  //     hideNavBar: true
  //   }
  // },
  {
    path: '/pay',
    name: 'Pay',
    component: () => import('@/views/Pay'),
    meta: {
      title: '订单支付 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '订单支付 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网订单支付页面。',
        keywords: '订单支付,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/model-publish',
    name: 'ModelPublish',
    component: () => import('@/views/ModelPublish'),
    meta: {
      title: '发布模型 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireSellerApproved: true,
      requireAuth: true,
      head: makeHead({
        title: '发布模型 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网发布3D模型页面。',
        keywords: '发布模型,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/model-publish/step2',
    name: 'ModelPublishStep2',
    component: () => import('@/views/ModelPublish/step2'),
    meta: {
      title: '发布模型 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireSellerApproved: true,
      requireAuth: true,
      head: makeHead({
        title: '发布模型 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网发布3D模型第二步。',
        keywords: '发布模型,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/scene-publish',
    name: 'ScenePublish',
    component: () => import('@/views/ScenePublish'),
    meta: {
      title: '发布点云 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireSellerApproved: true,
      requireAuth: true,
      head: makeHead({
        title: '发布点云 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网发布点云数据页面。',
        keywords: '发布点云,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/scene-publish/step2',
    name: 'ScenePublishStep2',
    component: () => import('@/views/ScenePublish/step2'),
    meta: {
      title: '发布点云 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireSellerApproved: true,
      requireAuth: true,
      head: makeHead({
        title: '发布点云 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网发布点云数据第二步。',
        keywords: '发布点云,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/product-publish',
    name: 'ProductPublish',
    component: () => import('@/views/ProductPublish'),
    meta: {
      title: '发布作品 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireSellerApproved: true,
      requireAuth: true,
      head: makeHead({
        title: '发布作品 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网发布3D定制作品页面。',
        keywords: '发布作品,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/product-publish/step2',
    name: 'ProductPublishStep2',
    component: () => import('@/views/ProductPublish/step2'),
    meta: {
      title: '发布作品 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireSellerApproved: true,
      requireAuth: true,
      head: makeHead({
        title: '发布作品 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网发布3D定制作品第二步。',
        keywords: '发布作品,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/product-detail/:id',
    name: 'ProductDetail',
    component: () => import('@/views/ProductDetail'),
    meta: {
      title: '作品详情 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
      head: makeHead({
        title: '作品详情 | 点云研智网-三维软件开发 三维建模 点云处理 3D打印等定制产品服务',
        description: '查看3D定制作品详情，了解作品内容、价格与作者信息。',
        keywords: '作品详情,3D定制,定制服务,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/author-detail/:id',
    name: 'AuthorDetail',
    component: () => import('@/views/UserCenter/AuthorDetail'),
    meta: {
      title: '作者详情 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '作者详情 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '查看作者主页，了解作者的3D定制作品与点云数字资产。',
        keywords: '作者详情,作者主页,3D定制,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/topic/detail/:id',
    name: 'TopicDetail',
    component: () => import('@/views/Topic/Detail'),
    meta: {
      title: '专题详情 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '专题详情 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网专题详情页，精选3D定制作品与点云数字资产合集。',
        keywords: '专题详情,3D定制作品,点云数字资产,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/topic/list',
    name: 'TopicList',
    component: () => import('@/views/Topic/list'),
    meta: {
      title: '专题列表 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '专题列表 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网专题列表，浏览全部3D定制作品与点云数字资产专题合集。',
        keywords: '专题列表,3D定制专题,点云专题,点云研智网',
        path: '/topic/list'
      })
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login'),
    meta: {
      title: '登录 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '登录 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网登录页面。',
        keywords: '登录,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/bind-pay-wechat',
    name: 'BindPayAlipay',
    component: () => import('@/views/BindPayWeChat'),
    meta: {
      title: '绑定支付宝 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      requireAuth: true,
      head: makeHead({
        title: '绑定支付宝 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网绑定支付宝页面。',
        keywords: '绑定支付宝,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register'),
    meta: {
      title: '注册 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '注册 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网注册页面。',
        keywords: '注册,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/About'),
    meta: {
      title: '关于点云研智网 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '关于点云研智网 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '了解点云研智网，专业的3D定制服务与点云数字资产交易平台，由合肥中研智设科技有限公司运营。',
        keywords: '关于点云研智网,合肥中研智设科技,3D定制平台,点云数字资产平台',
        path: '/about'
      })
    }
  },
  {
    path: '/site/help-service',
    name: 'Agreement',
    component: () => import('@/views/Agreement'),
    meta: {
      title: '协议 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '协议 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网用户协议与服务条款。',
        keywords: '用户协议,服务条款,点云研智网'
      })
    }
  },
  {
    path: '/more/detail',
    name: 'MoreDetail',
    component: () => import('@/views/MoreDetail'),
    meta: {
      title: '详情 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      head: makeHead({
        title: '详情 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网详情内容页。',
        keywords: '详情,点云研智网'
      })
    }
  },
  {
    path: '/bind-wx-quick-login',
    name: 'BindWxQuickLogin',
    component: () => import('@/views/BindWxQuickLogin'),
    meta: {
      title: '微信登录处理 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      hideNavBar: true,
      head: makeHead({
        title: '微信登录处理 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网微信登录处理页面。',
        keywords: '微信登录,点云研智网',
        noindex: true
      })
    }
  },
  {
    path: '/after-sales',
    name: 'AfterSales',
    component: () => import('@/views/AfterSales/index.vue'),
    meta: {
      title: '申请售后 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      requireAuth: true,
      head: makeHead({
        title: '申请售后 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网申请售后服务页面。',
        keywords: '申请售后,售后服务,点云研智网',
        noindex: true
      })
    }
  },
  // 个人中心路由（全部 noindex，私人页面不应被收录）
  {
    path: '/user-center',
    name: 'UserCenter',
    component: () => import('@/views/UserCenter'),
    meta: {
      title: '个人中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
      requireAuth: true,
      head: makeHead({
        title: '个人中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台',
        description: '点云研智网个人中心页面。',
        keywords: '个人中心,点云研智网',
        noindex: true
      })
    },
    redirect: '/user-center/buyer',
    children: [
      // 买家中心路由
      {
        path: 'buyer',
        name: 'BuyerHome',
        component: () => import('@/views/UserCenter/BuyerHome'),
        meta: { title: '买家中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '买家中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/orders',
        name: 'BuyerOrders',
        component: () => import('@/views/UserCenter/BuyerOrders'),
        meta: { title: '我的订单 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '我的订单 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/recharge',
        name: 'BuyerRecharge',
        component: () => import('@/views/UserCenter/BuyerHome'),
        meta: { title: '充值 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '充值 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/funds',
        name: 'BuyerFunds',
        component: () => import('@/views/UserCenter/BuyerHome'),
        meta: { title: '资金明细 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '资金明细 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/invoice',
        name: 'BuyerInvoice',
        component: () => import('@/views/UserCenter/BuyerInvoice'),
        meta: { title: '发票管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '发票管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/footprints',
        name: 'BuyerFootprints',
        component: () => import('@/views/UserCenter/BuyerFootprints'),
        meta: { title: '我的足迹 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '我的足迹 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/favorites',
        name: 'BuyerFavorites',
        component: () => import('@/views/UserCenter/BuyerFavorites'),
        meta: { title: '我的收藏 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '我的收藏 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/custom-orders',
        name: 'BuyerCustomOrders',
        component: () => import('@/views/UserCenter/BuyerCustomOrders'),
        meta: { title: '定制订单 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '定制订单 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/settings',
        name: 'BuyerSettings',
        component: () => import('@/views/UserCenter/BuyerSettings'),
        meta: { title: '账号设置 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '账号设置 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'buyer/security',
        name: 'BuyerSecurity',
        component: () => import('@/views/UserCenter/BuyerHome'),
        meta: { title: '安全中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '安全中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      // 卖家中心路由
      {
        path: 'seller',
        name: 'SellerHome',
        component: () => import('@/views/UserCenter/SellerHome'),
        meta: { title: '卖家中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台', head: makeHead({ title: '卖家中心 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      // {
      //   path: 'seller/promotion',
      //   name: 'SellerPromotion',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '限时特惠 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      {
        path: 'seller/models',
        name: 'SellerModels',
        component: () => import('@/views/UserCenter/SellerModels'),
        meta: { title: '模型管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '模型管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'seller/scenes',
        name: 'SellerScenes',
        component: () => import('@/views/UserCenter/SellerScenes'),
        meta: { title: '点云管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '点云管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'seller/products',
        name: 'SellerProducts',
        component: () => import('@/views/UserCenter/SellerProducts'),
        meta: { title: '作品管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '作品管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      // {
      //   path: 'seller/works',
      //   name: 'SellerWorks',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '作品管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/albums',
      //   name: 'SellerAlbums',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '专辑管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/copyright',
      //   name: 'SellerCopyright',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '版权保护 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/violations',
      //   name: 'SellerViolations',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '违规记录 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      {
        path: 'seller/transactions',
        name: 'SellerTransactions',
        component: () => import('@/views/UserCenter/SellerTransactions'),
        meta: { title: '交易汇总 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '交易汇总 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      // {
      //   path: 'seller/funds',
      //   name: 'SellerFunds',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '资金明细 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/authorization',
      //   name: 'SellerAuthorization',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '授权记录 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/reviews',
      //   name: 'SellerReviews',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '评价管理 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/data-overview',
      //   name: 'SellerDataOverview',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '数据概况 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      // {
      //   path: 'seller/search-ranking',
      //   name: 'SellerSearchRanking',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '搜索排行 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // },
      {
        path: 'seller/withdraw',
        name: 'SellerWithdraw',
        component: () => import('@/views/UserCenter/SellerWithdraw'),
        meta: { title: '提现 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '提现 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'seller/grab-orders',
        name: 'SellerGrabOrders',
        component: () => import('@/views/UserCenter/SellerGrabOrders'),
        meta: { title: '抢定制单 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '抢定制单 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      {
        path: 'seller/my-custom',
        name: 'SellerMyCustom',
        component: () => import('@/views/UserCenter/SellerMyCustom'),
        meta: { title: '我的定制 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true, head: makeHead({ title: '我的定制 | 点云研智网-专业3D定制服务与点云数字资产交易平台', noindex: true }) }
      },
      // {
      //   path: 'seller/info',
      //   name: 'SellerInfo',
      //   component: () => import('@/views/UserCenter/SellerHome'),
      //   meta: { title: '商户信息 | 点云研智网-专业3D定制服务与点云数字资产交易平台', requireSeller: true }
      // }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // eslint-disable-next-line no-unused-vars
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 }
  }
})

// 全局路由守卫 - 设置页面标题和权限校验
import { hasToken } from '@/utils/auth'

router.beforeEach(async (to, from, next) => {
  // 设置页面标题
  if (to.meta.title) {
    document.title = to.meta.title
  }

  // 检查是否需要登录
  if (to.matched.some(record => record.meta.requireAuth)) {
    if (!hasToken()) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }
  }

  // 检查是否需要卖家权限
  if (to.meta.requireSeller || to.meta.requireSellerApproved) {
    let userInfo = store.state.user.userInfo

    // 如果有 token 但没有用户信息，先获取用户信息
    const tokenExists = hasToken()
    if (!userInfo && tokenExists) {
      try {
        userInfo = await store.dispatch('user/getUserInfo')
      } catch (error) {
        console.error('获取用户信息失败:', error)
      }
    }

    // 没有登录
    if (!tokenExists) {
        next({
            path: '/login',
            query: { redirect: to.fullPath }
        })
        return
    }

    // roleType 不为 1 表示普通用户，需要跳转到入驻页面
    if (!userInfo || userInfo.roleType !== '1') {
      next({ path: '/seller-join' })
      return
    }

    if (to.meta.requireSellerApproved) {
      let settleStatus = store.state.user.settleStatus
      if (settleStatus === null || settleStatus === undefined) {
        try {
          const res = await getSettleStatus()
          settleStatus = res && res.data ? res.data.status : null
          store.commit('user/SET_SETTLE_STATUS', settleStatus)
        } catch (error) {
          console.error('获取入驻状态失败:', error)
        }
      }

      if (settleStatus !== '1') {
        Message({
          message: '入驻审核未通过，暂不能发布',
          type: 'warning',
          duration: 3000
        })
        next({ path: '/seller-join' })
        return
      }
    }
  }

  next()
})

// 路由后置钩子：在导航完成后异步推送当前 URL 给百度
// 用 afterEach 而非 beforeEach，避免推送失败影响导航
// 详情页（带 id）是动态内容，必须主动推送百度才能被快速发现
router.afterEach((to) => {
  // 详情页才主动推送，避免低价值页占用百度每日配额
  const isDetailPage = /-detail|works-detail|topic\/detail|custom-service-detail|author-detail/.test(to.path)
  if (isDetailPage && typeof window !== 'undefined') {
    // 延迟推送，不阻塞页面渲染
    setTimeout(() => {
      try {
        pushCurrentUrl(to)
      } catch (e) {
        // 推送失败静默处理，不影响业务
        console.warn('[baiduPush] 路由后置推送失败：', e.message)
      }
    }, 1000)
  }
})

export default router
